/**
 * Local Bridge MarketFit: define a product's inputs and benefits, each with category.
 * Uses ProductDefinitionForm for the form content.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Card, Button, Space } from 'antd';
import { SolutionOutlined, CloseOutlined } from '@ant-design/icons';
import { getMarketFitProduct, updateMarketFitProduct } from '../../services/localBridgeSessionApi';
import {
  parseProductInputsBenefits,
  parseProductSchemaV2,
  getMarketFitSchemaVersion,
  defaultProductSchemaV2,
  type ProductInputsBenefits,
  type ProductSchemaV2,
} from '../../types/marketFitLocalBridge';
import { ProductDefinitionForm } from '../MarketFit/ProductDefinitionForm';
import { message } from 'antd';

interface ProductDefinitionDrawerProps {
  open: boolean;
  productId: string | null;
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
  variant?: 'drawer' | 'panel';
}

export const ProductDefinitionDrawer: React.FC<ProductDefinitionDrawerProps> = ({
  open,
  productId,
  companyId,
  onClose,
  onSaved,
  variant = 'drawer',
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productName, setProductName] = useState('');
  const [data, setData] = useState<ProductInputsBenefits>({ inputs: [], benefits: [] });
  const [schemaMode, setSchemaMode] = useState<'legacy' | 'v2'>('legacy');
  const [dataV2, setDataV2] = useState<ProductSchemaV2>(defaultProductSchemaV2());

  const loadProduct = useCallback(async () => {
    if (!productId) return;
    if (variant === 'drawer' && !open) return;
    setLoading(true);
    try {
      const p = await getMarketFitProduct(productId);
      if (p) {
        setProductName(p.name);
        const json = p.inputs_benefits_json ?? '';
        setData(parseProductInputsBenefits(json));
        setDataV2(parseProductSchemaV2(json));
        setSchemaMode(getMarketFitSchemaVersion(json) === 2 ? 'v2' : 'legacy');
      } else {
        setProductName('');
        setData({ inputs: [], benefits: [] });
        setDataV2(defaultProductSchemaV2());
        setSchemaMode('legacy');
      }
    } finally {
      setLoading(false);
    }
  }, [open, productId, variant]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleSave = async () => {
    if (!productId) return;
    const payload = {
      inputs_benefits_json:
        schemaMode === 'v2'
          ? JSON.stringify(dataV2)
          : JSON.stringify({
              inputs: data.inputs.filter((i) => i.name?.trim()),
              benefits: data.benefits.filter((b) => b.name?.trim()),
            }),
    };
    setSaving(true);
    try {
      const updated = await updateMarketFitProduct(productId, payload);
      if (updated) {
        message.success('Product definition saved');
        if (schemaMode === 'legacy') {
          setData({
            inputs: data.inputs.filter((i) => i.name?.trim()),
            benefits: data.benefits.filter((b) => b.name?.trim()),
          });
        }
        onSaved();
      } else {
        message.error('Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const formContent = (
    <ProductDefinitionForm
      data={data}
      dataV2={dataV2}
      schemaMode={schemaMode}
      onDataChange={setData}
      onDataV2Change={setDataV2}
      onSchemaModeChange={setSchemaMode}
      onSave={handleSave}
      loading={loading}
      saving={saving}
      productId={productId}
      showFooter={false}
    />
  );

  const footer = (
    <Space>
      <Button onClick={onClose} icon={variant === 'panel' ? <CloseOutlined /> : undefined}>
        Close
      </Button>
      <Button type="primary" loading={saving} onClick={handleSave}>
        Save
      </Button>
    </Space>
  );

  if (variant === 'panel') {
    return (
      <Card
        title={
          <Space>
            <SolutionOutlined />
            <span>Define product: {productName || '…'}</span>
          </Space>
        }
        extra={variant === 'panel' ? <Button type="text" icon={<CloseOutlined />} onClick={onClose} /> : null}
        style={{ height: '100%' }}
      >
        {formContent}
        <div style={{ marginTop: 16 }}>{footer}</div>
      </Card>
    );
  }

  return (
    <Drawer
      title={
        <Space>
          <SolutionOutlined />
          <span>Define product: {productName || '…'}</span>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={640}
      footer={footer}
    >
      {formContent}
    </Drawer>
  );
};

export default ProductDefinitionDrawer;
